import React, { useEffect, useState } from 'react';
import { listLevels, archiveLevel } from '../../../api/levels';
import ExcelDownloadButton from '../../../components/common/ExcelDownloadButton';
import { getLevelListExportColumns } from '../../../utils/listExportColumns';
import LevelTable from '../../../components/levels/LevelTable';
import { useNavigate } from 'react-router-dom';
import PaginationFooter from '../../../components/common/PaginationFooter';
import useSortedClientPagination from '../../../hooks/useSortedClientPagination';
import { levelListSortColumns } from '../../../config/tableSortColumns';
import Button from '../../../components/common/Button';
import ListPageLayout, {
    ListPageHeader,
    ListFilterTabs,
    ListSearchCard,
    ListLoadingSpinner,
    ListEmptyState,
    ListTableCard,
} from '../../../components/layout/ListPageLayout';
import { showConfirm } from '../../../utils/popup';
import { useTranslation } from 'react-i18next';

// tiny debounce like Managers/Coaches
function useDebounced(value, delay = 200) {
    const [v, setV] = useState(value);
    useEffect(() => { const t = setTimeout(() => setV(value), delay); return () => clearTimeout(t); }, [value, delay]);
    return v;
}

const AdminLevelList = () => {
    const { t } = useTranslation();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState(null);
    const [filter, setFilter] = useState('active'); // 'all' | 'active' | 'archived'
    const [q, setQ] = useState('');
    const dq = useDebounced(q, 200);
    const navigate = useNavigate();

    const fetchData = async () => {
        setLoading(true);
        try {
            const res = await listLevels('ADMIN', {
                page: 0, size: 500,
                active: undefined
            });
            const list = res?.data?.content ?? res?.data ?? [];
            setRows(list);
            setErr(null);
        } catch (e) {
            console.error(e);
            setErr(t('pages.levels.loadFailed'));
            setRows([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const onEdit = (row) => navigate(`/admin/levels/${row.levelId || row.id}/edit`);
    const onArchive = async (row) => {
        if (!await showConfirm(t('pages.levels.archiveConfirm'))) return;
        try {
            await archiveLevel('ADMIN', row.levelId || row.id);
            fetchData();
        } catch (e) {
            console.error(e);
            alert(t('pages.levels.archiveFailed'));
        }
    };

    const filtered = React.useMemo(() => {
        let result = rows;

        // Apply filter tabs
        if (filter === 'active') {
            result = result.filter(r => r.active !== false);
        } else if (filter === 'archived') {
            result = result.filter(r => r.active === false);
        }
        // 'all' shows everything

        // Apply search query
        const term = (dq || '').trim().toLowerCase();
        if (term) {
            const pick = (x) => String(x ?? '').toLowerCase();
            result = result.filter(r => {
                const hay = [
                    r.name, r.description, r.branchName, r.branchId, r.id, r.levelId
                ].map(pick).join(' ');
                return term.split(/\s+/).every(t => hay.includes(t));
            });
        }

        return result;
    }, [rows, dq, filter]);

    const pagination = useSortedClientPagination(filtered, {
        columns: levelListSortColumns,
        resetKey: `${filter}-${dq}`,
    });

    const shown = filtered.length;
    const total = filter === 'all' ? rows.length : filter === 'active' ? rows.filter(r => r.active !== false).length : rows.filter(r => r.active === false).length;

    const levelFilterTabs = [
        { key: 'all', label: t('common.all'), count: rows.length },
        { key: 'active', label: t('common.active'), count: rows.filter(r => r.active !== false).length },
        { key: 'archived', label: t('common.archived'), count: rows.filter(r => r.active === false).length },
    ];

    return (
        <ListPageLayout>
            <ListPageHeader
                title={t('pages.levels.title')}
                subtitle={t('pages.levels.subtitle')}
                actions={(
                    <>
                        <ExcelDownloadButton
                            columns={getLevelListExportColumns(t)}
                            data={filtered}
                            filename="levels"
                            disabled={filtered.length === 0}
                        />
                        <button
                            onClick={() => navigate('/admin/levels/new')}
                            className="btn-page-action-primary"
                        >
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        {t('pages.levels.addLevel')}
                    </button>
                    </>
                )}
            />

            <ListFilterTabs tabs={levelFilterTabs} active={filter} onChange={setFilter} />

            <ListSearchCard
                value={q}
                onChange={setQ}
                placeholder={t('pages.levels.searchPlaceholder')}
                shown={shown}
                total={total}
                entityLabel={t('pages.levels.entityLabel')}
            />

            {loading ? (
                <ListLoadingSpinner />
            ) : err ? (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">{err}</div>
            ) : filtered.length === 0 ? (
                <ListEmptyState
                    message={q ? t('pages.levels.noMatch') : t('pages.levels.empty')}
                    hint={q ? t('common.emptySearchHint') : undefined}
                />
            ) : (
                <ListTableCard>
                    <LevelTable
                        data={pagination.pageItems}
                        role="ADMIN"
                        onEdit={onEdit}
                        onArchive={onArchive}
                        sortKey={pagination.sortKey}
                        sortDir={pagination.sortDir}
                        onSort={pagination.toggleSort}
                    />
                </ListTableCard>
            )}

            {filtered.length > 0 && (
            <PaginationFooter
                page={pagination.page}
                size={pagination.size}
                total={pagination.total}
                onPageChange={pagination.setPage}
                onSizeChange={pagination.setSize}
            />
            )}
        </ListPageLayout>
    );
};

export default AdminLevelList;
